/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  HStack,
  VStack,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import { OrderTable } from '.';
import { GetAllUnSettledOrdersQuery, Order, Settlement } from 'src/utils/__generated__/graphql';
import { FiAlertCircle, FiDollarSign, FiPackage, FiShoppingBag, FiTruck } from 'react-icons/fi';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface UnsettledOrdersProps {
  data?: GetAllUnSettledOrdersQuery;
}

interface AccumulatedRestaurantOrder {
  restaurant: Order['basket']['restaurant'];
  orders_aggregate: {
    aggregate: {
      count: number;
    };
  };
  dishAmount: number;
  totalDeliveryCharge: number;
  totalAdminEarning: number;
  totalRestaurantEarning: number;
  created_at: string;
}

const UnsettledOrders: React.FC<UnsettledOrdersProps> = ({ data }) => {
  const orders = data?.orders as Order[] | undefined;

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const statBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  const {
    unsettledRestaurantList,
    totalOrders,
    totalAmount,
    totalFoodAmount,
    totalDeliveryAmount,
    adminEarning,
    adminFoodEarning,
    adminDeliveryEarning,
    restaurantEarning,
    restaurantFoodEarning,
    restaurantDeliveryEarning,
  } = useMemo(() => {
    if (!orders || orders.length === 0) {
      return {
        unsettledRestaurantList: [],
        totalOrders: 0,
        totalAmount: 0,
        totalFoodAmount: 0,
        totalDeliveryAmount: 0,
        adminEarning: 0,
        adminFoodEarning: 0,
        adminDeliveryEarning: 0,
        restaurantEarning: 0,
        restaurantFoodEarning: 0,
        restaurantDeliveryEarning: 0,
      };
    }

    const restaurantOrders = orders.reduce<Record<string, AccumulatedRestaurantOrder>>((acc, order) => {
      const restaurant = order?.basket?.restaurant ?? { id: '', admin_commission: 0 };
      const { id, admin_commission } = restaurant;
      const { subTotal = 0, deliveryCharge = 0, deliveredBy = '' } = order ?? {};

      const commission = ((admin_commission ?? 0) / 100) * subTotal + (deliveredBy === 'ADMIN' ? deliveryCharge : 0);
      const earnings = subTotal + deliveryCharge - commission;

      if (!acc[id]) {
        acc[id] = {
          restaurant: { ...restaurant },
          orders_aggregate: {
            aggregate: { count: 0 },
          },
          dishAmount: 0,
          totalDeliveryCharge: 0,
          totalAdminEarning: 0,
          totalRestaurantEarning: 0,
          created_at: order.created_at,
        };
      }

      acc[id].orders_aggregate.aggregate.count += 1;
      acc[id].dishAmount += subTotal;
      acc[id].totalDeliveryCharge += deliveryCharge;
      acc[id].totalAdminEarning += commission;
      acc[id].totalRestaurantEarning += earnings;

      return acc;
    }, {});

    const unsettledRestaurantList = Object.values(restaurantOrders);

    const totalOrders = orders.length;
    const totalAmount = orders.reduce((sum, order) => sum + order.subTotal + order.deliveryCharge, 0);
    const totalFoodAmount = orders.reduce((sum, order) => sum + order.subTotal, 0);
    const totalDeliveryAmount = orders.reduce((sum, order) => sum + order.deliveryCharge, 0);

    const adminEarning = orders.reduce((sum, order) => {
      const commission = ((order?.basket?.restaurant?.admin_commission ?? 0) / 100) * order.subTotal;
      const deliveryEarning = order.deliveredBy === 'ADMIN' ? order.deliveryCharge : 0;
      return sum + commission + deliveryEarning;
    }, 0);

    const adminFoodEarning = orders.reduce(
      (sum, order) => sum + ((order?.basket?.restaurant?.admin_commission ?? 0) / 100) * order.subTotal,
      0
    );

    const adminDeliveryEarning = orders.reduce(
      (sum, order) => sum + (order.deliveredBy === 'ADMIN' ? order.deliveryCharge : 0),
      0
    );

    const restaurantEarning = totalAmount - adminEarning;
    const restaurantFoodEarning = totalFoodAmount - adminFoodEarning;
    const restaurantDeliveryEarning = totalDeliveryAmount - adminDeliveryEarning;

    return {
      unsettledRestaurantList,
      totalOrders,
      totalAmount,
      totalFoodAmount,
      totalDeliveryAmount,
      adminEarning,
      adminFoodEarning,
      adminDeliveryEarning,
      restaurantEarning,
      restaurantFoodEarning,
      restaurantDeliveryEarning,
    };
  }, [orders]);

  if (!orders || orders.length === 0) {
    return (
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Card borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} bg={cardBg} p={6}>
          <Flex direction="column" align="center" justify="center" py={10}>
            <Icon as={FiAlertCircle as any} boxSize={12} color="gray.400" mb={4} />
            <Heading size="md" color={textColor} mb={2}>
              No Unsettled Orders
            </Heading>
            <Text color="gray.500">There are no unsettled orders available at this time.</Text>
          </Flex>
        </Card>
      </MotionBox>
    );
  }

  return (
    <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
        bg={cardBg}
        mb={8}
        overflow="hidden"
      >
        <CardHeader bg={headerBg} py={4} px={6}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'start', md: 'center' }}
            gap={4}
          >
            <HStack>
              <Icon as={FiAlertCircle as any} color="orange.500" boxSize={5} />
              <Heading as="h4" size="md">
                Unsettled Orders
              </Heading>
              <Badge colorScheme="orange" fontSize="sm" borderRadius="full">
                {totalOrders} orders
              </Badge>
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody p={6}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiShoppingBag as any} color={accentColor} boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Total Orders
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color={accentColor}>
                  {totalOrders}
                </StatNumber>
                <StatHelpText>
                  <Text fontSize="sm">Pending settlement</Text>
                </StatHelpText>
              </Stat>
            </MotionFlex>

            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiDollarSign as any} color="green.500" boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Total Amount
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color="green.500">
                  Rs. {totalAmount.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs">Food: Rs. {totalFoodAmount.toFixed(2)}</Text>
                    <Text fontSize="xs">Delivery: Rs. {totalDeliveryAmount.toFixed(2)}</Text>
                  </VStack>
                </StatHelpText>
              </Stat>
            </MotionFlex>

            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiPackage as any} color="purple.500" boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Admin Earning
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color="purple.500">
                  Rs. {adminEarning.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs">Food: Rs. {adminFoodEarning.toFixed(2)}</Text>
                    <Text fontSize="xs">Delivery: Rs. {adminDeliveryEarning.toFixed(2)}</Text>
                  </VStack>
                </StatHelpText>
              </Stat>
            </MotionFlex>

            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiTruck as any} color="blue.500" boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Restaurant Earning
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color="blue.500">
                  Rs. {restaurantEarning.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs">Food: Rs. {restaurantFoodEarning.toFixed(2)}</Text>
                    <Text fontSize="xs">Delivery: Rs. {restaurantDeliveryEarning.toFixed(2)}</Text>
                  </VStack>
                </StatHelpText>
              </Stat>
            </MotionFlex>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} bg={cardBg} overflow="hidden">
        <CardHeader bg={headerBg} py={4} px={6}>
          <HStack>
            <Icon as={FiShoppingBag as any} color={accentColor} boxSize={5} />
            <Heading as="h4" size="md">
              Restaurant Settlement Details
            </Heading>
          </HStack>
        </CardHeader>

        <CardBody p={0}>
          <Box overflowX="auto">
            <OrderTable orders={unsettledRestaurantList as Settlement[]} orderType="unsettled" />
          </Box>
        </CardBody>
      </Card>
    </MotionBox>
  );
};

export default UnsettledOrders;
